package com.karyadina.service

import com.karyadina.model.ImageItem
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.await
import org.khronos.webgl.ArrayBuffer
import org.khronos.webgl.Uint8Array
import org.w3c.dom.HTMLInputElement
import org.w3c.files.Blob
import org.w3c.files.File
import org.w3c.files.FileReader
import kotlin.coroutines.resume
import kotlin.coroutines.suspendCoroutine
import kotlin.js.Promise

actual class FileServiceImpl : FileService {
    
    // Web implementation does not support camera capture directly
    actual override suspend fun takePhoto(): ImageItem? {
        return null
    }

    private var fileInputElement: HTMLInputElement? = null
    
    actual override suspend fun pickImages(maxCount: Int): List<ImageItem> {
        return suspendCoroutine { continuation ->
            // Create hidden file input
            val input = (document.createElement("input") as HTMLInputElement).apply {
                type = "file"
                accept = "image/*"
                multiple = true
                style.display = "none"
            }
            
            input.onchange = { event ->
                val files = input.files
                if (files != null && files.length > 0) {
                    val imageItems = mutableListOf<ImageItem>()
                    val count = minOf(files.length, maxCount)
                    
                    for (i in 0 until count) {
                        val file = files.item(i)
                        if (file != null && file.type.startsWith("image/")) {
                            imageItems.add(
                                ImageItem(
                                    id = "${file.name}_${file.lastModified}",
                                    name = file.name,
                                    uri = createObjectURL(file),
                                    sizeInBytes = file.size.toLong(),
                                    mimeType = file.type
                                )
                            )
                        }
                    }
                    continuation.resume(imageItems)
                } else {
                    continuation.resume(emptyList())
                }
                document.body?.removeChild(input)
            }
            
            input.oncancel = {
                continuation.resume(emptyList())
                document.body?.removeChild(input)
            }
            
            document.body?.appendChild(input)
            input.click()
        }
    }
    
    actual override suspend fun pickImage(): ImageItem? {
        val images = pickImages(1)
        return images.firstOrNull()
    }
    
    actual override suspend fun pickPdf(): String? {
        return suspendCoroutine { continuation ->
            val input = (document.createElement("input") as HTMLInputElement).apply {
                type = "file"
                accept = "application/pdf"
                style.display = "none"
            }
            
            input.onchange = {
                val file = input.files?.item(0)
                if (file != null) {
                    continuation.resume(createObjectURL(file))
                } else {
                    continuation.resume(null)
                }
                document.body?.removeChild(input)
            }
            
            input.oncancel = {
                continuation.resume(null)
                document.body?.removeChild(input)
            }
            
            document.body?.appendChild(input)
            input.click()
        }
    }
    
    actual override suspend fun saveFile(uri: String, fileName: String): Boolean {
        return try {
            // Create download link and trigger download
            val link = document.createElement("a").apply {
                setAttribute("href", uri)
                setAttribute("download", fileName)
                setAttribute("style", "display: none")
            }
            document.body?.appendChild(link)
            (link as org.w3c.dom.HTMLAnchorElement).click()
            document.body?.removeChild(link)
            true
        } catch (e: Exception) {
            console.error("Failed to save file", e)
            false
        }
    }
    
    actual override suspend fun shareFile(uri: String, mimeType: String) {
        try {
            // Try Web Share API first
            if (js("navigator.share !== undefined") as Boolean) {
                val shareData = js("({url: uri})")
                js("navigator.share(shareData)").unsafeCast<Promise<Unit>>().await()
            } else {
                // Fallback: download the file
                saveFile(uri, "shared_file")
            }
        } catch (e: Exception) {
            console.error("Failed to share file", e)
        }
    }
    
    actual override suspend fun getFileInfo(uri: String): FileInfo? {
        return try {
            // For blob URLs created by the app, we can't get much info
            // Return basic info
            FileInfo(
                name = uri.substringAfterLast("/"),
                uri = uri,
                sizeInBytes = 0L,
                mimeType = "application/octet-stream"
            )
        } catch (e: Exception) {
            null
        }
    }
    
    private fun createObjectURL(file: File): String {
        return js("URL.createObjectURL(file)") as String
    }
}
